/* Sohi — collection page. Progressive enhancement over collection.filters /
   collection.sort_options / {% paginate %}: opens the mobile filters drawer,
   auto-submits the filter form on every change so results update without a
   "go" button, and lets the active-filter chips remove themselves. Every
   control still works with this file absent — plain GET links and forms. */
(function () {
  "use strict";

  // Targets this section's own [data-filters-drawer], never [data-cart-drawer]
  // — that shell belongs to the cart-drawer phase and is left alone here.
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function initDrawer(section) {
    var drawer = document.querySelector("[data-filters-drawer]");
    var trigger = section.querySelector("[data-filters-trigger]");
    if (!drawer) return;

    var lastFocused = null;
    var lockedScrollY = 0;

    // The panel declares role="dialog" aria-modal="true", so it has to behave
    // like one: freeze the page behind it, move focus in, keep Tab inside it,
    // and hand focus back on close. Deliberately a local copy of the cart
    // drawer's pair (sections/sohi-cart-drawer.liquid) rather than a shared
    // import — the collection page loads no cart JS at all, and the demo's
    // assets/sohi-cart.js is demo-only and never served by the theme.
    //
    // Both drawers write the same html[data-scroll-lock], so neither may
    // unlock under the other: the cart drawer can be opened from the header
    // while the filters panel is up (a quick-add from the grid behind it
    // dispatches cart:open), and whichever closed second would otherwise
    // restore scrolling while the other is still modal — and, worse, scroll
    // the page back to a stale offset.
    function cartDrawerOpen() {
      return !!document.querySelector("[data-cart-drawer][data-open]");
    }

    function lockScroll() {
      var root = document.documentElement;
      if (root.hasAttribute("data-scroll-lock")) return; // cart already locked it
      lockedScrollY = window.pageYOffset || root.scrollTop || 0;
      document.body.style.top = -lockedScrollY + "px";
      root.setAttribute("data-scroll-lock", "");
    }

    function unlockScroll() {
      var root = document.documentElement;
      if (!root.hasAttribute("data-scroll-lock")) return;
      if (cartDrawerOpen()) return; // still modal behind us — leave it locked
      root.removeAttribute("data-scroll-lock");
      document.body.style.top = "";
      var prev = root.style.scrollBehavior;
      root.style.scrollBehavior = "auto"; // html has scroll-behavior:smooth
      window.scrollTo(0, lockedScrollY);
      root.style.scrollBehavior = prev;
    }

    function trap(e) {
      var panel = drawer.querySelector(".drawer__panel");
      if (!panel) return;
      var items = Array.prototype.filter.call(
        panel.querySelectorAll(FOCUSABLE),
        function (n) { return n.offsetParent !== null; }
      );
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    function open() {
      lastFocused = document.activeElement;
      lockScroll();
      drawer.inert = false;
      drawer.setAttribute("data-open", "");
      var closeBtn = drawer.querySelector(".drawer__close");
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      if (!drawer.hasAttribute("data-open")) return;
      drawer.removeAttribute("data-open");
      drawer.inert = true;
      unlockScroll();
      // Back where it came from, not lost to <body> — otherwise the next Tab
      // restarts at the top of the document. document.contains() guard: the
      // panel's own "View results" button is a closer, and a filter change
      // navigates the page out from under us.
      if (lastFocused && document.contains(lastFocused) && typeof lastFocused.focus === "function") {
        lastFocused.focus();
      }
      lastFocused = null;
    }

    // .drawer is visibility:hidden while closed, which already takes its
    // controls out of the tab order; `inert` is belt-and-braces and, unlike a
    // CSS change, cannot regress the cart drawer that shares the .drawer rules.
    drawer.inert = !drawer.hasAttribute("data-open");

    if (trigger) trigger.addEventListener("click", open);
    drawer.querySelectorAll("[data-drawer-close]").forEach(function (el) {
      el.addEventListener("click", close);
    });
    document.addEventListener("keydown", function (e) {
      if (!drawer.hasAttribute("data-open")) return;
      // The cart drawer has its own document-level Escape handler. When it is
      // open it is the topmost dialog and owns the key; closing both on one
      // press would be wrong.
      if (cartDrawerOpen()) return;
      if (e.key === "Escape") close();
      else if (e.key === "Tab") trap(e);
    });
  }

  function submitFilterForm(form) {
    if (!form) return;
    // Empty min/max inputs would otherwise add empty query params
    // (?filter.v.price.gte=) that collection.filters treats as active.
    // The disable must not outlive the submit call. requestSubmit() builds
    // the form's entry list synchronously while dispatching the submit event,
    // so restoring immediately afterwards can never re-add those empty params
    // — and leaving them disabled strands the field: .filters__price input has
    // no :disabled style, so a dead price box looks pixel-identical to a live
    // one and the visitor just finds it silently un-typeable. That is reachable
    // without ever touching the price filter, because this runs on ANY change
    // in the form (the first checkbox tick disables both empty price inputs),
    // and it survives on screen whenever the navigation does not happen:
    // interactive validation rejecting a value over the max="" below, or Back
    // into a bfcache snapshot. try/finally so a requestSubmit() that throws
    // (NotSupportedError when the form is already submitting, on rapid
    // successive changes) still restores them.
    var muted = [];
    form.querySelectorAll('input[type="number"]').forEach(function (input) {
      if (input.value === "") {
        input.disabled = true;
        muted.push(input);
      }
    });
    try {
      form.requestSubmit();
    } finally {
      // Only the ones we disabled: the list filters render zero-count values
      // disabled server-side (sections/sohi-collection.liquid), and those must
      // stay that way.
      muted.forEach(function (input) { input.disabled = false; });
    }
  }

  // Backstop for a bfcache restore that snapshotted the page mid-submit, so a
  // Back never lands on a dead price box. Registered at top level, not in
  // init(): the section's data-sohi-ready flag survives in the restored DOM,
  // so init() does not run again on a bfcache restore. Keep the
  // input[type="number"] qualifier — broadening it to [disabled] would
  // wrongly re-enable the server-disabled zero-count filter checkboxes.
  window.addEventListener("pageshow", function (e) {
    if (!e.persisted) return;
    document
      .querySelectorAll('[data-collection-filters] input[type="number"]:disabled')
      .forEach(function (input) { input.disabled = false; });
  });

  // Delegated rather than bound straight to the <form>: the sort <select>
  // lives in the results bar next to the grid, outside the sidebar form, and
  // is associated with it only via the form="" attribute (it can't be a
  // descendant — the grid sits between them and each product card has its
  // own quick-add form, so the sidebar form can't wrap the whole layout).
  // A change event on the select still bubbles through its own DOM ancestors
  // (which are inside `section`), not through the form it's attached to, so
  // this reads the true owner off e.target.form rather than the listener's
  // own element.
  function onFilterChange(e) {
    if (!e.target.matches("input, select")) return;
    var form = e.target.form;
    if (!form || !form.matches("[data-collection-filters]")) return;
    submitFilterForm(form);
  }

  function initForms(section) {
    section.addEventListener("change", onFilterChange);
    var drawer = document.querySelector("[data-filters-drawer]");
    if (drawer) drawer.addEventListener("change", onFilterChange);
  }

  function initChips(section) {
    section.querySelectorAll("[data-remove-filter]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        window.location.href = btn.getAttribute("data-remove-filter");
      });
    });
  }

  // Grid/list layout switch. Purely presentational — it never re-queries the
  // collection, so it stays client-side and is remembered across pages in
  // localStorage rather than costing a page load like the filters do.
  var VIEW_KEY = "sohi:collection-view";

  function applyView(section, view) {
    var grid = section.querySelector(".shop__grid");
    if (grid) grid.classList.toggle("shop__grid--list", view === "list");
    section.querySelectorAll("[data-shop-view]").forEach(function (btn) {
      btn.setAttribute("aria-pressed", btn.getAttribute("data-shop-view") === view ? "true" : "false");
    });
  }

  function readView() {
    try {
      return window.localStorage.getItem(VIEW_KEY) === "list" ? "list" : "grid";
    } catch (e) {
      return "grid";
    }
  }

  function initViews(section) {
    var buttons = section.querySelectorAll("[data-shop-view]");
    if (!buttons.length) return;

    buttons.forEach(function (btn) {
      btn.addEventListener("click", function () {
        var view = btn.getAttribute("data-shop-view") === "list" ? "list" : "grid";
        try { window.localStorage.setItem(VIEW_KEY, view); } catch (e) { /* private mode */ }
        applyView(section, view);
      });
    });

    applyView(section, readView());
  }

  function init(section) {
    if (section.dataset.sohiReady === "true") return;
    section.dataset.sohiReady = "true";

    initDrawer(section);
    initForms(section);
    initChips(section);
    initViews(section);
  }

  function boot(root) {
    (root || document).querySelectorAll("[data-collection]").forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { boot(); });
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
})();
